import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export const useProjectContracts = (projectId: string | undefined) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["contracts", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contracts")
        .select("*")
        .eq("project_id", projectId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user && !!projectId,
  });
};

export const useContractSovLines = (contractId: string | undefined) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["sov_lines", contractId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sov_lines")
        .select("*")
        .eq("contract_id", contractId!)
        .order("line_number");
      if (error) throw error;
      return data;
    },
    enabled: !!user && !!contractId,
  });
};

export const useProjectWorkOrders = (projectId: string | undefined, pagination?: import("./usePagination").PaginationState) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["work_orders", projectId, pagination?.page],
    queryFn: async () => {
      if (pagination) {
        const { count } = await supabase
          .from("work_orders")
          .select("id", { count: "exact", head: true })
          .eq("project_id", projectId!);
        pagination.setTotalCount(count ?? 0);
      }

      let query = supabase
        .from("work_orders")
        .select("*, vendors(name)")
        .eq("project_id", projectId!)
        .order("created_at", { ascending: false });

      if (pagination) {
        query = query.range(pagination.from, pagination.to);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: !!user && !!projectId,
  });
};

export const useWorkOrderLines = (projectId: string | undefined) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["work_order_lines", projectId],
    queryFn: async () => {
      // First get WO ids for this project
      const { data: wos, error: woErr } = await supabase
        .from("work_orders")
        .select("id")
        .eq("project_id", projectId!);
      if (woErr) throw woErr;
      const woIds = (wos ?? []).map((w) => w.id);
      if (!woIds.length) return [];

      const { data, error } = await supabase
        .from("work_order_lines")
        .select("*, work_orders(id, number, status, contractor_id, vendors(name)), sov_lines(tracking_type), unit_schedule(unit_number)")
        .in("work_order_id", woIds);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user && !!projectId,
  });
};

export const useContractors = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["contractors"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contractor_profiles")
        .select("id, name")
        .eq("is_deleted", false)
        .order("name");
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
};
